/**
 * Medivigil - Stacking Engine
 * Combines multiple medication evaluations and applies modifiers
 * 
 * DETERMINISTIC: Same input always produces same output
 * NO AI/LLM: Pure rule-based stacking
 */

import { normalizeMedications, getNormalizationSummary } from './normalizationEngine';
import { evaluateMedication, getHigherSeverity, escalateSeverity, SEVERITY_ORDER } from './ruleEngine';

/**
 * Risk domain display names
 */
export const DOMAIN_LABELS = {
  bleeding: 'Bleeding Risk',
  mronj: 'MRONJ Risk',
  infection: 'Infection Risk',
  healing: 'Healing Risk',
  anesthetic: 'Anesthetic Considerations',
  cardiac: 'Cardiac Considerations'
};

/**
 * Modifier types and their effects
 */
export const MODIFIER_DEFINITIONS = {
  INR_ELEVATED: {
    id: 'INR_ELEVATED',
    label: 'INR > 3.5',
    description: 'Elevated INR increases bleeding risk',
    affectsDomains: ['bleeding'],
    escalates: true
  },
  RENAL_IMPAIRMENT: {
    id: 'RENAL_IMPAIRMENT',
    label: 'Renal Impairment',
    description: 'Reduced renal function may prolong drug effects',
    affectsDomains: ['bleeding'],
    escalates: true
  },
  IV_ANTIRESORPTIVE: {
    id: 'IV_ANTIRESORPTIVE',
    label: 'IV Antiresorptive (Oncology)',
    description: 'IV administration for oncology indication increases MRONJ risk',
    affectsDomains: ['mronj'],
    escalates: true
  },
  ACTIVE_CHEMOTHERAPY: {
    id: 'ACTIVE_CHEMOTHERAPY',
    label: 'Active Chemotherapy',
    description: 'Current chemotherapy increases infection and healing risks',
    affectsDomains: ['infection', 'healing', 'mronj'],
    escalates: true
  },
  POORLY_CONTROLLED_DIABETES: {
    id: 'POORLY_CONTROLLED_DIABETES',
    label: 'Poorly Controlled Diabetes (HbA1c > 8)',
    description: 'Poor glycemic control impairs healing',
    affectsDomains: ['healing', 'infection'],
    escalates: true
  },
  DUAL_ANTIPLATELET: {
    id: 'DUAL_ANTIPLATELET',
    label: 'Dual Antiplatelet Therapy',
    description: 'Combined antiplatelet agents increase bleeding risk',
    affectsDomains: ['bleeding'],
    escalates: true
  },
  LONG_TERM_BISPHOSPHONATE: {
    id: 'LONG_TERM_BISPHOSPHONATE',
    label: 'Bisphosphonate > 4 years',
    description: 'Long-term use increases MRONJ risk',
    affectsDomains: ['mronj'],
    escalates: true
  },
  PACEMAKER: {
    id: 'PACEMAKER',
    label: 'Pacemaker',
    description: 'Pacemaker present — ultrasonic scalers and electrosurgery contraindicated',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'ORANGE',
    baseRecommendation: 'Patient has a pacemaker. Avoid ultrasonic scalers and electrosurgical devices. Confirm device type and manufacturer guidance before proceeding.',
    baseAdvisoryNote: 'Use hand scalers or air-driven instruments. Liaise with cardiologist if uncertain about device compatibility.'
  },
  ICD: {
    id: 'ICD',
    label: 'Implantable Cardioverter-Defibrillator (ICD)',
    description: 'ICD present — similar precautions to pacemaker apply',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'ORANGE',
    baseRecommendation: 'Patient has an ICD. Avoid ultrasonic scalers and electrosurgical devices. Confirm device programming with cardiologist for complex procedures.',
    baseAdvisoryNote: 'Treat as pacemaker precautions. Consider cardiology liaison before invasive procedures.'
  },
  HYPERTENSION: {
    id: 'HYPERTENSION',
    label: 'Hypertension',
    description: 'Elevated blood pressure may affect procedure safety and anesthetic management',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'YELLOW',
    baseRecommendation: 'Patient has hypertension. Monitor blood pressure pre-procedure. Limit epinephrine in local anesthetic where appropriate.',
    baseAdvisoryNote: 'Poorly controlled hypertension may warrant medical review before elective procedures.'
  },
  HISTORY_OF_STROKE: {
    id: 'HISTORY_OF_STROKE',
    label: 'History of Stroke / TIA',
    description: 'Prior cerebrovascular event — may indicate ongoing anticoagulation and increased cardiac risk',
    affectsDomains: ['cardiac', 'bleeding'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'ORANGE',
    baseRecommendation: 'Patient has a history of stroke or TIA. Verify current anticoagulation status. Consider medical consultation for elective procedures.',
    baseAdvisoryNote: 'Assess concurrent antiplatelet or anticoagulant therapy. Bleeding risk may be elevated.'
  },
  CARDIAC_SURGERY_HISTORY: {
    id: 'CARDIAC_SURGERY_HISTORY',
    label: 'History of Cardiac Surgery',
    description: 'Prior cardiac surgery — assess for prosthetic valves or implanted devices',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'ORANGE',
    baseRecommendation: 'Patient has a history of cardiac surgery. Assess for prosthetic valves requiring antibiotic prophylaxis. Medical consultation recommended for invasive procedures.',
    baseAdvisoryNote: 'Confirm whether antibiotic prophylaxis is indicated. Check current cardiac medications and device status.'
  },
  HEART_FAILURE: {
    id: 'HEART_FAILURE',
    label: 'Heart Failure',
    description: 'Reduced cardiac output impairs tissue perfusion and healing',
    affectsDomains: ['cardiac', 'healing'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'ORANGE',
    baseRecommendation: 'Patient has heart failure. Healing may be impaired. Avoid prolonged supine positioning. Medical consultation recommended for major procedures.',
    baseAdvisoryNote: 'Position patient semi-upright. Minimise procedure duration and physiological stress.'
  },
  RECENT_MI: {
    id: 'RECENT_MI',
    label: 'Recent Myocardial Infarction (< 6 months)',
    description: 'Recent MI significantly increases perioperative cardiac risk',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'RED',
    baseRecommendation: 'Patient has had a recent myocardial infarction. Elective dental procedures should be deferred until cardiology clearance is obtained.',
    baseAdvisoryNote: 'High perioperative risk. Emergency care only if necessary. Coordinate with cardiologist before any intervention.'
  },
  ARRHYTHMIA: {
    id: 'ARRHYTHMIA',
    label: 'Arrhythmia / Atrial Fibrillation',
    description: 'Cardiac arrhythmia may affect response to stress and vasoconstrictors',
    affectsDomains: ['cardiac'],
    escalates: true,
    creates: true,
    baseRiskLevel: 'YELLOW',
    baseRecommendation: 'Patient has a cardiac arrhythmia. Limit epinephrine in local anesthetic. Monitor patient during procedure.',
    baseAdvisoryNote: 'Assess rate control and anticoagulation status. Patients with atrial fibrillation are often anticoagulated — verify bleeding risk.'
  }
};

/**
 * Evaluates all medications and stacks results by domain
 * @param {string[]} medications - Raw medication inputs
 * @param {string} traumaLevel - Trauma level ('T1', 'T2', 'T3')
 * @param {string[]} activeModifiers - Array of modifier IDs
 * @returns {object} - Complete stacked evaluation
 */
export function evaluateAllMedications(medications, traumaLevel, activeModifiers = []) {
  // Step 1: Normalize all medications
  const normalizedResults = normalizeMedications(medications);
  const normalizationSummary = getNormalizationSummary(normalizedResults);
  
  // Step 2: Evaluate each normalized medication
  const evaluations = normalizedResults
    .filter(r => r.status === 'MATCHED')
    .map(r => evaluateMedication(r.normalized, traumaLevel));
  
  // Step 3: Stack results by domain
  const stackedByDomain = stackByDomain(evaluations);
  
  // Step 4: Apply modifiers
  const modifiedResults = applyModifiers(stackedByDomain, activeModifiers);
  
  // Step 5: Generate final output
  return {
    input: {
      medications,
      traumaLevel,
      activeModifiers
    },
    normalization: normalizationSummary,
    evaluations,
    domainResults: modifiedResults,
    unclassifiedMedications: normalizationSummary.unclassifiedInputs,
    timestamp: new Date().toISOString()
  };
}

/**
 * Stacks evaluation results by risk domain
 * Returns highest severity for each domain
 */
function stackByDomain(evaluations) {
  const domainResults = {};
  
  for (const evaluation of evaluations) {
    if (evaluation.status !== 'EVALUATED') continue;
    
    for (const [domain, rule] of Object.entries(evaluation.rulesByDomain)) {
      if (!domainResults[domain]) {
        domainResults[domain] = {
          domain,
          label: DOMAIN_LABELS[domain] || domain,
          severity: rule.riskLevel,
          contributingMedications: [evaluation.medication],
          rules: [rule],
          recommendation: rule.recommendation,
          advisoryNote: rule.advisoryNote || rule.clinicalAction, // Support both old and new format
          modifierApplied: false
        };
      } else {
        // Compare and keep higher severity
        const existingSeverity = domainResults[domain].severity;
        const newSeverity = rule.riskLevel;
        
        if (SEVERITY_ORDER.indexOf(newSeverity) > SEVERITY_ORDER.indexOf(existingSeverity)) {
          domainResults[domain].severity = newSeverity;
          domainResults[domain].recommendation = rule.recommendation;
          domainResults[domain].advisoryNote = rule.advisoryNote || rule.clinicalAction;
        }
        
        // Add medication to contributors if not already present
        if (!domainResults[domain].contributingMedications.includes(evaluation.medication)) {
          domainResults[domain].contributingMedications.push(evaluation.medication);
        }
        domainResults[domain].rules.push(rule);
      }
    }
  }
  
  return domainResults;
}

/**
 * Applies modifiers to domain results
 * Modifiers can escalate severity by one level
 */
function applyModifiers(domainResults, activeModifiers) {
  const modifiedResults = { ...domainResults };
  
  for (const modifierId of activeModifiers) {
    const modifier = MODIFIER_DEFINITIONS[modifierId];
    if (!modifier) continue;
    
    for (const affectedDomain of modifier.affectsDomains) {
      if (!modifiedResults[affectedDomain] && modifier.creates) {
        modifiedResults[affectedDomain] = {
          domain: affectedDomain,
          label: DOMAIN_LABELS[affectedDomain] || affectedDomain,
          severity: modifier.baseRiskLevel,
          contributingMedications: [],
          rules: [],
          recommendation: modifier.baseRecommendation,
          advisoryNote: modifier.baseAdvisoryNote,
          modifierApplied: true,
          appliedModifiers: [modifier.label]
        };
      } else if (modifiedResults[affectedDomain] && modifier.escalates) {
        const currentSeverity = modifiedResults[affectedDomain].severity;
        const escalatedSeverity = escalateSeverity(currentSeverity);

        if (escalatedSeverity !== currentSeverity) {
          modifiedResults[affectedDomain] = {
            ...modifiedResults[affectedDomain],
            severity: escalatedSeverity,
            severityBeforeModifier: currentSeverity,
            modifierApplied: true,
            appliedModifiers: [
              ...(modifiedResults[affectedDomain].appliedModifiers || []),
              modifier.label
            ]
          };
        }
      }
    }
  }
  
  return modifiedResults;
}

/**
 * Gets the overall highest severity across all domains
 */
export function getOverallSeverity(domainResults) {
  let highest = 'GREEN';
  
  for (const result of Object.values(domainResults)) {
    highest = getHigherSeverity(highest, result.severity);
  }
  
  return highest;
}

/**
 * Formats results for display
 * Implements deterministic logic for unrecognized medications:
 * - If ALL medications unrecognized: show UNKNOWN status, require manual review
 * - If SOME medications unrecognized: show warning, assessment may be incomplete
 * - If ALL medications recognized and low-risk: show LOW RISK
 */
export function formatResultsForDisplay(evaluationResult) {
  const { domainResults, unclassifiedMedications, input, normalization } = evaluationResult;
  
  const totalMedications = input.medications.length;
  const recognizedCount = normalization.matched;
  const unrecognizedCount = normalization.unclassified;
  
  const formattedDomains = Object.values(domainResults).map(domain => ({
    domain: domain.label,
    severity: domain.severity,
    severityLabel: getSeverityLabel(domain.severity),
    medications: domain.contributingMedications.join(', '),
    recommendation: domain.recommendation,
    advisoryNote: domain.advisoryNote,
    modifierApplied: domain.modifierApplied,
    appliedModifiers: domain.appliedModifiers || []
  }));
  
  // Determine assessment status based on recognition
  let assessmentStatus = 'COMPLETE';
  let assessmentWarning = null;
  let overallSeverity = getOverallSeverity(domainResults);
  
  if (totalMedications === 0) {
    // No medications entered
    assessmentStatus = 'NO_INPUT';
    assessmentWarning = 'No medications entered.';
    overallSeverity = 'UNKNOWN';
  } else if (unrecognizedCount === totalMedications) {
    // ALL medications unrecognized
    assessmentStatus = 'ALL_UNRECOGNIZED';
    assessmentWarning = 'No recognized medications detected. Manual clinical review required.';
    overallSeverity = 'UNKNOWN';
  } else if (unrecognizedCount > 0) {
    // SOME medications unrecognized
    assessmentStatus = 'PARTIAL';
    assessmentWarning = 'Some medications were not recognized. Risk assessment may be incomplete.';
    // Keep the calculated severity but flag incompleteness
  }
  // If all recognized, assessmentStatus remains 'COMPLETE'
  
  return {
    traumaLevel: input.traumaLevel,
    traumaLevelLabel: getTraumaLevelLabel(input.traumaLevel),
    overallSeverity,
    assessmentStatus,
    assessmentWarning,
    domains: formattedDomains,
    unclassified: unclassifiedMedications,
    hasUnclassified: unclassifiedMedications.length > 0,
    recognizedCount,
    unrecognizedCount,
    totalMedications
  };
}

/**
 * Gets human-readable severity label
 */
export function getSeverityLabel(severity) {
  const labels = {
    GREEN: 'Low Risk',
    YELLOW: 'Moderate Risk',
    ORANGE: 'Elevated Risk',
    RED: 'High Risk',
    UNKNOWN: 'Review Required'
  };
  return labels[severity] || severity;
}

/**
 * Gets human-readable trauma level label
 */
export function getTraumaLevelLabel(traumaLevel) {
  const labels = {
    T1: 'Minimal Trauma (T1)',
    T2: 'Moderate Trauma (T2)',
    T3: 'High Trauma (T3)'
  };
  return labels[traumaLevel] || traumaLevel;
}

export default {
  evaluateAllMedications,
  getOverallSeverity,
  formatResultsForDisplay,
  getSeverityLabel,
  getTraumaLevelLabel,
  DOMAIN_LABELS,
  MODIFIER_DEFINITIONS
};
